import { transceive } from './actions';

/**
 * Firmware capability detection utilities
 * Detects device firmware version and supported algorithms
 */

/**
 * Parse firmware version string
 * @param {string} versionHex - Hex string from device
 * @returns {string} Version string (e.g., "2.0.1")
 */
function parseVersion(versionHex) {
  if (!versionHex || versionHex.length < 6) {
    return 'Unknown';
  }

  try {
    // Remove status word if present
    const data = versionHex.endsWith('9000') ? versionHex.slice(0, -4) : versionHex;

    // Version format: major.minor.patch (3 bytes)
    if (data.length >= 6) {
      const major = parseInt(data.substring(0, 2), 16);
      const minor = parseInt(data.substring(2, 4), 16);
      const patch = parseInt(data.substring(4, 6), 16);
      return `${major}.${minor}.${patch}`;
    }

    return 'Unknown';
  } catch (error) {
    console.error('Failed to parse version:', error);
    return 'Unknown';
  }
}

/**
 * Parse PIV algorithm extension response
 * Based on piv.h:25-33 structure
 */
function parseAlgorithmExtension(response) {
  if (!response.endsWith('9000')) {
    // Old firmware doesn't support this command
    return {
      enabled: false,
      ed25519: false,
      rsa3072: false,
      rsa4096: false,
      x25519: false,
      secp256k1: false,
      sm2: false
    };
  }

  try {
    const data = response.slice(0, -4);

    if (data.length < 14) {
      return { enabled: false };
    }

    // Parse packed structure
    return {
      enabled: data.substring(0, 2) === '01',
      ed25519: data.substring(2, 4) === '01',
      rsa3072: data.substring(4, 6) === '01',
      rsa4096: data.substring(6, 8) === '01',
      x25519: data.substring(8, 10) === '01',
      secp256k1: data.substring(10, 12) === '01',
      sm2: data.substring(12, 14) === '01'
    };
  } catch (error) {
    console.error('Failed to parse algorithm extension:', error);
    return { enabled: false };
  }
}

/**
 * Detect PIV firmware capabilities
 * @param {Function} dispatch - Redux dispatch function
 * @returns {Object} Capability information
 */
export async function detectPIVCapabilities(dispatch) {
  try {
    // Select PIV applet
    await dispatch(transceive('00A4040009A00000030800001000'));

    // Get firmware version (INS 0xFD)
    const versionRes = await dispatch(transceive('00FD000000'));
    const version = parseVersion(versionRes);

    // Get algorithm extension support (INS 0xEE)
    let algExt = { enabled: false };
    try {
      const algExtRes = await dispatch(transceive('00EE000000'));
      algExt = parseAlgorithmExtension(algExtRes);
    } catch (error) {
      // Old firmware doesn't support this command
      console.warn('Algorithm extension not supported:', error);
    }

    // Build capability object
    const capabilities = {
      version: version,
      applet: 'PIV',
      algorithms: {
        // Always supported
        rsa2048: true,
        ecc256: true,
        ecc384: true,

        // Extended algorithms (require firmware support)
        rsa3072: algExt.rsa3072 || false,
        rsa4096: algExt.rsa4096 || false,
        ed25519: algExt.ed25519 || false,
        x25519: algExt.x25519 || false,
        secp256k1: algExt.secp256k1 || false,
        sm2: algExt.sm2 || false
      },
      features: {
        algorithmExtension: algExt.enabled,
        metadata: true, // Assume supported if we got version
        touchPolicy: true,
        pinPolicy: true
      }
    };

    return capabilities;
  } catch (error) {
    console.error('Failed to detect PIV capabilities:', error);
    return {
      version: 'Unknown',
      applet: 'PIV',
      error: error.message,
      algorithms: {
        rsa2048: true,
        ecc256: true,
        ecc384: true
      },
      features: {}
    };
  }
}

/**
 * Detect OpenPGP firmware capabilities
 * @param {Function} dispatch - Redux dispatch function
 * @returns {Object} Capability information
 */
export async function detectOpenPGPCapabilities(dispatch) {
  try {
    // Select OpenPGP applet
    await dispatch(transceive('00A4040006D27600012401'));

    // Get application related data (TAG 0x6E)
    const appDataRes = await dispatch(transceive('00CA006E00'));

    let version = 'Unknown';
    let algorithms = {
      rsa2048: true,
      rsa3072: false,
      rsa4096: false,
      ecc256: true,
      ecc384: false,
      ed25519: false,
      cv25519: false
    };

    if (appDataRes.endsWith('9000')) {
      const data = appDataRes.slice(0, -4);

      // Parse application data to extract version and algorithm attributes
      // This is a simplified parser - full implementation would parse all TLV tags

      // Look for algorithm attributes tags (C1, C2, C3)
      let offset = 0;
      while (offset < data.length - 4) {
        const tag = data.substring(offset, offset + 2);
        offset += 2;

        if (offset >= data.length) break;

        const len = parseInt(data.substring(offset, offset + 2), 16);
        offset += 2;

        if (tag === 'C1' || tag === 'C2' || tag === 'C3') {
          // Algorithm attributes
          if (len > 0 && offset + len * 2 <= data.length) {
            const algId = data.substring(offset, offset + 2);

            // Parse algorithm ID
            switch (algId) {
              case '01': // RSA
                const rsaLen = parseInt(data.substring(offset + 2, offset + 6), 16);
                if (rsaLen === 2048) algorithms.rsa2048 = true;
                if (rsaLen === 3072) algorithms.rsa3072 = true;
                if (rsaLen === 4096) algorithms.rsa4096 = true;
                break;
              case '12': // ECDH
              case '13': // ECDSA
                algorithms.ecc256 = true;
                break;
              case '16': // EDDSA
                algorithms.ed25519 = true;
                break;
            }
          }
        }

        offset += len * 2;
      }
    }

    return {
      version: version,
      applet: 'OpenPGP',
      algorithms: algorithms,
      features: {
        keyImport: true,
        keyGeneration: true,
        uif: true // User Interaction Flag
      }
    };
  } catch (error) {
    console.error('Failed to detect OpenPGP capabilities:', error);
    return {
      version: 'Unknown',
      applet: 'OpenPGP',
      error: error.message,
      algorithms: {
        rsa2048: true,
        ecc256: true
      },
      features: {}
    };
  }
}

/**
 * Detect device serial number
 * @param {Function} dispatch - Redux dispatch function
 * @returns {string} Serial number
 */
export async function getDeviceSerial(dispatch) {
  try {
    // Try PIV GET SERIAL (INS 0xF8)
    await dispatch(transceive('00A4040009A00000030800001000'));
    const serialRes = await dispatch(transceive('00F8000000'));

    if (serialRes.endsWith('9000')) {
      const serial = serialRes.slice(0, -4);
      if (serial && !/^0+$/.test(serial)) {
        return serial;
      }
    }

    return 'Unknown';
  } catch (error) {
    console.error('Failed to get device serial:', error);
    return 'Unknown';
  }
}

/**
 * Get complete device information
 * @param {Function} dispatch - Redux dispatch function
 * @returns {Object} Complete device info
 */
export async function getDeviceInfo(dispatch) {
  try {
    const [pivCap, openpgpCap, serial] = await Promise.all([
      detectPIVCapabilities(dispatch),
      detectOpenPGPCapabilities(dispatch),
      getDeviceSerial(dispatch)
    ]);

    return {
      serial: serial,
      piv: pivCap,
      openpgp: openpgpCap,
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    console.error('Failed to get device info:', error);
    return {
      serial: 'Unknown',
      error: error.message,
      timestamp: new Date().toISOString()
    };
  }
}

/**
 * Check if specific algorithm is supported
 * @param {Object} capabilities - Capability object from detectPIVCapabilities
 * @param {string} algorithm - Algorithm ID (e.g., '07', '11', 'E0')
 * @returns {boolean} True if supported
 */
export function isAlgorithmSupported(capabilities, algorithm) {
  if (!capabilities || !capabilities.algorithms) {
    return false;
  }

  const algMap = {
    '05': 'rsa3072',
    '07': 'rsa2048',
    '16': 'rsa4096',
    '11': 'ecc256',
    '14': 'ecc384',
    'E0': 'ed25519',
    'E1': 'x25519',
    '53': 'secp256k1',
    '54': 'sm2'
  };

  const algName = algMap[algorithm.toUpperCase()];
  return algName ? capabilities.algorithms[algName] === true : false;
}

/**
 * Get algorithm display name
 * @param {string} algorithmId - Algorithm ID
 * @returns {string} Display name
 */
export function getAlgorithmName(algorithmId) {
  const names = {
    '05': 'RSA-3072',
    '07': 'RSA-2048',
    '16': 'RSA-4096',
    '11': 'ECC P-256',
    '14': 'ECC P-384',
    'E0': 'Ed25519',
    'E1': 'X25519',
    '53': 'secp256k1',
    '54': 'SM2'
  };

  return names[algorithmId.toUpperCase()] || 'Unknown';
}

/**
 * Get recommended algorithms based on capabilities
 * @param {Object} capabilities - Capability object
 * @returns {Array} Array of recommended algorithm IDs
 */
export function getRecommendedAlgorithms(capabilities) {
  const recommended = [];

  if (!capabilities || !capabilities.algorithms) {
    return ['07', '11']; // Default: RSA-2048, ECC P-256
  }

  // Prefer modern algorithms if supported
  if (capabilities.algorithms.ed25519) {
    recommended.push('E0'); // Ed25519 (fastest, modern)
  }

  if (capabilities.algorithms.ecc256) {
    recommended.push('11'); // ECC P-256 (good balance)
  }

  if (capabilities.algorithms.rsa2048) {
    recommended.push('07'); // RSA-2048 (most compatible)
  }

  return recommended.length > 0 ? recommended : ['07', '11'];
}

/**
 * Format capability info for display
 * @param {Object} capabilities - Capability object
 * @returns {Array} Array of capability items for UI
 */
export function formatCapabilitiesForDisplay(capabilities) {
  if (!capabilities || !capabilities.algorithms) {
    return [];
  }

  const items = [];

  // RSA algorithms
  if (capabilities.algorithms.rsa2048) {
    items.push({ id: '07', name: 'RSA-2048', supported: true, recommended: true });
  }
  if (capabilities.algorithms.rsa3072) {
    items.push({ id: '05', name: 'RSA-3072', supported: true, recommended: false });
  }
  if (capabilities.algorithms.rsa4096) {
    items.push({ id: '16', name: 'RSA-4096', supported: true, recommended: false });
  } else {
    items.push({ id: '16', name: 'RSA-4096', supported: false, requiresUpgrade: true });
  }

  // ECC algorithms
  if (capabilities.algorithms.ecc256) {
    items.push({ id: '11', name: 'ECC P-256', supported: true, recommended: true });
  }
  if (capabilities.algorithms.ecc384) {
    items.push({ id: '14', name: 'ECC P-384', supported: true, recommended: false });
  }

  // Modern algorithms
  if (capabilities.algorithms.ed25519) {
    items.push({ id: 'E0', name: 'Ed25519', supported: true, recommended: true });
  } else {
    items.push({ id: 'E0', name: 'Ed25519', supported: false, requiresUpgrade: true });
  }

  if (capabilities.algorithms.x25519) {
    items.push({ id: 'E1', name: 'X25519', supported: true, recommended: false });
  }

  return items;
}
